import { Fragment, useEffect, useMemo, useState } from 'react';
import { History, Loader2, Search } from 'lucide-react';
import { strategicKpisApi } from '../../services/strategicKpisApi';
import type { ActualsResponse, KpiDefinition } from '../../types/strategicKpis';
import {
    AGGREGATION_LABEL, CATEGORY_LABEL, CATEGORY_ORDER, UNIT_LABEL, WINDOW_TYPE_LABEL, targetStoredText, windowText,
} from '../../utils/strategicKpiFormat';
import { useBodyTableOffset } from './useTableOffset';

interface Props {
    /** The dashboard's current response, if loaded — gives every KPI its concrete window dates. */
    actuals: ActualsResponse | null;
    /** Opens the «Version history» panel (VersionHistoryPanel); the page passes it from D4 on. */
    onOpenVersions?: () => void;
}

const SOURCE: Record<string, string> = {
    crm: 'HubSpot.Deal, pipeline "Projects", deals created since 2022, owner = HubSpot owner of the person — via [Reporting].[vw_Kpi_DealFacts]',
    visits: 'DataWarehouse.HubspotMeetingView × [Reporting].[KpiActivityTypeMap], owner = HubSpot owner of the person — via [Reporting].[vw_Kpi_VisitFacts]',
    gp: 'etl.SalesTransactions × etl.Customer × etl.Product × etl.Country, person = sales manager of the customer — via [Reporting].[vw_Kpi_GpFacts]',
    bab: 'BabReporting.ActualByLineFreezeVersioned (latest freeze of the month) × EtlBab.LineDefinition — company / BU only',
    marketdata: 'HubSpot.Marketdata, owner = HubSpot owner of the person',
};

const DIRECTION_LABEL: Record<string, string> = { higher: 'higher is better', lower: 'lower is better (achievement = target ÷ actual)' };

const PILL_STYLE = { display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', gap: 8 } as const;
const CELL_TOP = { verticalAlign: 'top', paddingTop: 8, paddingBottom: 8 } as const;
const CELL_WRAP = { verticalAlign: 'top', paddingTop: 8, paddingBottom: 8, whiteSpace: 'normal' } as const;
const CELL_DEF = { verticalAlign: 'top', paddingTop: 8, paddingBottom: 8, whiteSpace: 'normal', minWidth: 320, maxWidth: 480 } as const;
const CELL_TARGET = { verticalAlign: 'top', paddingTop: 8, paddingBottom: 8, whiteSpace: 'normal', minWidth: 160 } as const;

/**
 * The KPI catalogue as reading text (masterplan skpi-redesign, package D2, E14): families on the left, the
 * entries of the chosen family in one table on the right (name, key, definition, window, unit, target levels,
 * version). The search runs over all KPIs; the family counts show the matches. The list of definition versions
 * lives in the «Version history» panel. Inactive (renamed) KPIs are not shown. The engine computes each KPI
 * according to its version (mode "reviewed"); mode "pbi-compatible" forces version 1, for comparison only.
 */
export function DefinitionsTab({ actuals, onOpenVersions }: Props) {
    const [defs, setDefs] = useState<KpiDefinition[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [family, setFamily] = useState<string>('all');
    const [search, setSearch] = useState('');

    useEffect(() => {
        let cancelled = false;
        strategicKpisApi.definitions()
            .then(r => { if (!cancelled) setDefs(r); })
            .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load the catalogue'); });
        return () => { cancelled = true; };
    }, []);

    const sorted = useMemo(() => {
        const rank = (c: string) => { const i = CATEGORY_ORDER.indexOf(c); return i < 0 ? CATEGORY_ORDER.length : i; };
        return [...(defs ?? [])].sort((a, b) => rank(a.Category) - rank(b.Category) || a.SortOrder - b.SortOrder);
    }, [defs]);

    const matches = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return sorted;
        return sorted.filter(d => `${d.Name} ${d.KpiKey} ${d.DefinitionText}`.toLowerCase().includes(q));
    }, [sorted, search]);

    const families = useMemo(() => [...new Set(sorted.map(d => d.Category))], [sorted]);
    const activeFamily = family === 'all' || families.includes(family) ? family : 'all';
    const rows = activeFamily === 'all' ? matches : matches.filter(d => d.Category === activeFamily);

    const windowByKey = useMemo(() => new Map((actuals?.kpis ?? []).map(k => [k.kpiKey, windowText(k, k.window)])), [actuals]);

    const { body, style: offsetStyle } = useBodyTableOffset([defs, activeFamily, rows.length]);

    if (error) return <div role="alert" className="banner">{error}</div>;
    if (!defs) return <div className="muted p-12 text-center" role="status"><Loader2 className="i sm mr-2 inline animate-spin" aria-hidden="true" />Loading the catalogue…</div>;

    return (
        <div className="flex flex-col gap-4">
            <div className="toolbar">
                <p className="sub m-0">
                    {defs.length} KPIs in the catalogue, computed by definition version (mode <code>{actuals?.mode ?? 'reviewed'}</code>).
                    Person and group targets are stored independently and may differ; the dashboard shows both.
                </p>
                <span className="tb-grow" />
                <div className="search">
                    <Search className="i sm" aria-hidden="true" />
                    <input
                        type="search"
                        className="input"
                        placeholder="Find a KPI"
                        aria-label="Find a KPI"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        data-testid="skpi-definitions-search"
                    />
                </div>
                {onOpenVersions && (
                    <button type="button" className="btn" onClick={onOpenVersions} data-testid="skpi-open-versions">
                        <History className="i sm" aria-hidden="true" />Version history
                    </button>
                )}
            </div>

            <div className="grid items-start gap-4" style={{ gridTemplateColumns: '200px minmax(0, 1fr)' }}>
                <div className="flex flex-col gap-2" role="group" aria-label="KPI family" data-testid="skpi-definitions-families">
                    <button type="button" className="pill" style={PILL_STYLE} aria-pressed={activeFamily === 'all'} onClick={() => setFamily('all')}>
                        All families <small>{matches.length}</small>
                    </button>
                    {families.map(f => (
                        <button key={f} type="button" className="pill" style={PILL_STYLE} aria-pressed={activeFamily === f} onClick={() => setFamily(f)}>
                            {CATEGORY_LABEL[f] ?? f} <small>{matches.filter(d => d.Category === f).length}</small>
                        </button>
                    ))}
                </div>

                <div ref={body} style={offsetStyle} className="flex min-w-0 flex-col gap-3">
                    {activeFamily !== 'all' && SOURCE[activeFamily] && (
                        <p className="sub m-0"><span className="strong">Source:</span> {SOURCE[activeFamily]}</p>
                    )}
                    {rows.length === 0 ? (
                        activeFamily !== 'all' && matches.length > 0 ? (
                            <div className="empty" data-testid="skpi-definitions-empty">
                                <b>No match in {CATEGORY_LABEL[activeFamily] ?? activeFamily}</b>
                                <p>{matches.length} {matches.length === 1 ? 'KPI matches' : 'KPIs match'} “{search.trim()}” in other families.</p>
                                <button type="button" className="btn" onClick={() => setFamily('all')} data-testid="skpi-definitions-all-families">All families</button>
                            </div>
                        ) : (
                            <div className="empty" data-testid="skpi-definitions-empty">
                                <b>No KPI found</b>
                                <p>Nothing in the catalogue matches “{search.trim()}”.</p>
                            </div>
                        )
                    ) : (
                        <div className="tbl-wrap" data-testid="skpi-definitions-table">
                            <table className="tbl">
                                <thead>
                                    <tr>
                                        <th scope="col">KPI</th>
                                        <th scope="col">Definition</th>
                                        <th scope="col">Window</th>
                                        <th scope="col">Unit</th>
                                        <th scope="col">Target levels</th>
                                        <th scope="col">Version</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((d, i) => (
                                        <Fragment key={d.KpiKey}>
                                        {activeFamily === 'all' && (i === 0 || rows[i - 1].Category !== d.Category) && (
                                            <tr className="grp" data-row-kind="group">
                                                <td colSpan={6} style={{ whiteSpace: 'normal' }}>
                                                    {CATEGORY_LABEL[d.Category] ?? d.Category}
                                                    {SOURCE[d.Category] && <span className="gsub">Source: {SOURCE[d.Category]}</span>}
                                                </td>
                                            </tr>
                                        )}
                                        <tr data-kpi={d.KpiKey}>
                                            <td className="key" style={CELL_TOP}>
                                                {d.Name}
                                                <div className="muted" style={{ fontWeight: 450 }}>{d.KpiKey}</div>
                                            </td>
                                            <td style={CELL_DEF}>
                                                {d.DefinitionText}
                                                <div className="muted">
                                                    {AGGREGATION_LABEL[d.GroupAggregation] ?? d.GroupAggregation} · {DIRECTION_LABEL[d.Direction] ?? d.Direction}
                                                    {d.LegacyColumn ? ` · etl.CrmTargets.${d.LegacyColumn}` : ''}{d.BusinessUnits ? ` · BU ${d.BusinessUnits}` : ''}
                                                </div>
                                            </td>
                                            <td style={CELL_WRAP}>
                                                {WINDOW_TYPE_LABEL[d.WindowType] ?? d.WindowType}
                                                {windowByKey.has(d.KpiKey) && <div className="muted">currently {windowByKey.get(d.KpiKey)}</div>}
                                            </td>
                                            <td style={CELL_TOP}>{UNIT_LABEL[d.Unit] ?? d.Unit}</td>
                                            <td style={CELL_TARGET}>{targetStoredText(d)}</td>
                                            <td className="num" style={CELL_TOP}>{d.DefinitionVersion}</td>
                                        </tr>
                                        </Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
